import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Plus, Pencil, Trash2, Upload, Download, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DataTable } from '@/components/ui/data-table';
import { Modal } from '@/components/ui/modal';
import { ActionButton } from '@/components/ui/action-button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/lib/supabase';
import type { School, Region, SchoolLevel } from '@/types';

interface FormData {
  name: string;
  region_id: string;
  school_level_id: string;
}

interface Filters {
  search: string;
  region: string;
  schoolLevel: string;
}

export default function Schools() {
  const [schools, setSchools] = useState<School[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [schoolLevels, setSchoolLevels] = useState<SchoolLevel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState<Filters>({
    search: '',
    region: '',
    schoolLevel: '',
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState<School | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    region_id: '',
    school_level_id: '',
  });

  useEffect(() => {
    fetchSchools();
    fetchRegions();
    fetchSchoolLevels();
  }, []);

  const fetchSchools = async () => {
    try {
      const { data, error } = await supabase
        .from('sms_schools')
        .select(`
          *,
          region:sms_regions(*),
          school_level:sms_school_levels(*)
        `)
        .order('name');

      if (error) throw error;
      setSchools(data);
    } catch (error) {
      console.error('Error fetching schools:', error);
      toast.error('Failed to fetch schools');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRegions = async () => {
    const { data, error } = await supabase
      .from('sms_regions')
      .select('*')
      .order('name');
    
    if (error) {
      console.error('Error fetching regions:', error);
      return;
    }
    setRegions(data);
  };

  const fetchSchoolLevels = async () => {
    const { data, error } = await supabase
      .from('sms_school_levels')
      .select('*')
      .order('name');
    
    if (error) {
      console.error('Error fetching school levels:', error);
      return;
    }
    setSchoolLevels(data);
  };

  const handleSubmit = async () => {
    try {
      if (selectedSchool) {
        // Update existing school
        const { error } = await supabase
          .from('sms_schools')
          .update(formData)
          .eq('id', selectedSchool.id);

        if (error) throw error;
        toast.success('School updated successfully');
      } else {
        // Create new school
        const { error } = await supabase
          .from('sms_schools')
          .insert(formData);

        if (error) throw error;
        toast.success('School created successfully');
      }

      setModalOpen(false);
      fetchSchools();
    } catch (error) {
      console.error('Error saving school:', error);
      toast.error('Failed to save school');
    }
  };

  const handleDelete = async () => {
    if (!selectedSchool) return;

    try {
      const { error } = await supabase
        .from('sms_schools')
        .delete()
        .eq('id', selectedSchool.id);

      if (error) throw error;

      toast.success('School deleted successfully');
      setDeleteDialogOpen(false);
      fetchSchools();
    } catch (error) {
      console.error('Error deleting school:', error);
      toast.error('Failed to delete school');
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = ''; // Reset file input

    try {
      const text = await file.text();
      const rows = text.split('\n')
        .map(row => row.trim())
        .filter(row => row.length > 0)
        .map(row => row.split(',').map(cell => cell.trim()));

      const headers = rows[0];

      // Validate CSV structure
      if (!headers.includes('name') || !headers.includes('region') || !headers.includes('level')) {
        toast.error('CSV must include name, region, and level columns');
        return;
      }

      let successCount = 0;
      let errorCount = 0;
      const schools = rows.slice(1).map(row => ({
        name: row[headers.indexOf('name')].trim(),
        region: row[headers.indexOf('region')].trim(),
        level: row[headers.indexOf('level')].trim(),
      }));

      // Process schools
      for (const school of schools) {
        const region = regions.find(r => r.name.toLowerCase() === school.region.toLowerCase());
        const level = schoolLevels.find(l => l.name.toLowerCase() === school.level.toLowerCase());

        if (!region || !level) {
          console.warn(`Skipping ${school.name}: Invalid region or level`);
          errorCount++;
          continue;
        }

        const { error } = await supabase.from('sms_schools').insert({
          name: school.name,
          region_id: region.id,
          school_level_id: level.id,
        });
        if (error) {
          console.error(`Error adding ${school.name}:`, error);
          errorCount++;
        } else {
          successCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully imported ${successCount} school${successCount !== 1 ? 's' : ''}`);
      }
      if (errorCount > 0) {
        toast.error(`Failed to import ${errorCount} school${errorCount !== 1 ? 's' : ''}`);
      }
      fetchSchools();
    } catch (error) {
      console.error('Error importing schools:', error);
      toast.error('Failed to import schools');
    }
  };

  const handleExportCSV = () => {
    const headers = ['name', 'region', 'level'];
    const rows = schools.map(school => [
      `"${(school.name || '').replace(/"/g, '""')}"`,
      `"${(school.region?.name || '').replace(/"/g, '""')}"`,
      `"${(school.school_level?.name || '').replace(/"/g, '""')}"`,
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `schools-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredSchools = schools.filter(school => {
    const matchesSearch = school.name.toLowerCase().includes(filters.search.toLowerCase());
    const matchesRegion = !filters.region || filters.region === 'all' || school.region_id === filters.region;
    const matchesLevel = !filters.schoolLevel || filters.schoolLevel === 'all' || school.school_level_id === filters.schoolLevel;
    return matchesSearch && matchesRegion && matchesLevel;
  });

  const columns = [
    { header: 'Name', accessorKey: 'name' },
    {
      header: 'Region',
      accessorKey: 'region',
      cell: ({ row }: any) => row.original.region?.name,
    },
    {
      header: 'School Level',
      accessorKey: 'school_level',
      cell: ({ row }: any) => row.original.school_level?.name,
    },
    {
      header: 'Actions',
      accessorKey: 'actions',
      cell: ({ row }: any) => (
        <div className="flex gap-2">
          <ActionButton
            icon={Pencil}
            variant="edit"
            onClick={() => {
              setSelectedSchool(row.original);
              setFormData({
                name: row.original.name,
                region_id: row.original.region_id,
                school_level_id: row.original.school_level_id,
              });
              setModalOpen(true);
            }}
          />
          <ActionButton
            icon={Trash2}
            variant="delete"
            onClick={() => {
              setSelectedSchool(row.original);
              setDeleteDialogOpen(true);
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="container mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Schools</h1>
          <p className="text-muted-foreground mt-1">Manage and organize schools</p>
        </div>
        <div className="flex flex-col md:flex-row gap-2">
          <input
            type="file"
            accept=".csv"
            className="hidden"
            id="csv-upload"
            onChange={handleFileUpload}
          />
          <Button variant="outline" onClick={handleExportCSV}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={() => document.getElementById('csv-upload')?.click()}>
            <Upload className="h-4 w-4 mr-2" />
            Import CSV
          </Button>
          <Button
            onClick={() => {
              setSelectedSchool(null);
              setFormData({
                name: '',
                region_id: '',
                school_level_id: '',
              });
              setModalOpen(true);
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add School
          </Button>
        </div>
      </div>

      <div className="bg-card border rounded-lg p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search schools..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="pl-9"
            />
          </div>
          <Select
            value={filters.region}
            onValueChange={(value) => setFilters({ ...filters, region: value })}
          >
            <SelectTrigger className="w-full md:w-[200px]">
              <SelectValue placeholder="Filter by region" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Regions</SelectItem>
              {regions.map((region) => (
                <SelectItem key={region.id} value={region.id}>
                  {region.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filters.schoolLevel}
            onValueChange={(value) => setFilters({ ...filters, schoolLevel: value })}
          >
            <SelectTrigger className="w-full md:w-[200px]">
              <SelectValue placeholder="Filter by level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Levels</SelectItem>
              {schoolLevels.map((level) => (
                <SelectItem key={level.id} value={level.id}>
                  {level.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => setFilters({ search: '', region: '', schoolLevel: '' })}
            className="w-full md:w-auto"
            disabled={!filters.search && !filters.region && !filters.schoolLevel}
          >
            Reset Filters
          </Button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredSchools}
        isLoading={isLoading}
      />

      <Modal
        title={selectedSchool ? 'Edit School' : 'Add School'}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleSubmit}
      >
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1 block">Name</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Region</label>
            <Select
              value={formData.region_id}
              onValueChange={(value) => setFormData({ ...formData, region_id: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a region" />
              </SelectTrigger>
              <SelectContent>
                {regions.map((region) => (
                  <SelectItem key={region.id} value={region.id}>
                    {region.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">School Level</label>
            <Select
              value={formData.school_level_id}
              onValueChange={(value) => setFormData({ ...formData, school_level_id: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a school level" />
              </SelectTrigger>
              <SelectContent>
                {schoolLevels.map((level) => (
                  <SelectItem key={level.id} value={level.id}>
                    {level.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Modal>

      <Modal
        title="Delete School"
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        confirmText="Delete"
        confirmVariant="destructive"
      >
        <p>Are you sure you want to delete this school? This action cannot be undone.</p>
      </Modal>
    </div>
  );
}